import { RefreshCw } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type {
  SocialCatalogProcessingStatus,
  SocialCatalogPushStatus,
  SocialCatalogStatus,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { CatalogIssuesTable } from './catalog-issues-table';
import { ChannelStep, StepBadge, StepRow, type StepTone } from './channel-step';

export interface CatalogStepProps {
  /** False until the network is connected; the step then only says so. */
  active: boolean;
  status: SocialCatalogStatus | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  editable: boolean;
  toggling: boolean;
  onToggle: (enabled: boolean) => void;
  syncing: boolean;
  onSync: () => void;
  /** Store time zone label for the daily fetch time. */
  timeZone: string;
  /** Step 2 is done. Not a gate: a domain claimed on Pinterest by hand works too. */
  websiteClaimed: boolean;
}

const RUN_TONE: Record<SocialCatalogProcessingStatus, StepTone> = {
  completed: 'done',
  failed: 'attention',
  processing: 'todo',
};

function Counts({ label, counts }: { label: string; counts: Record<string, number> }) {
  const { t } = useTranslation('social');
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  return (
    <StepRow label={label}>
      <ul className="flex flex-col gap-0.5">
        {entries.map(([code, count]) => (
          <li key={code} className="font-mono text-xs">
            {code}{' '}
            <span className="text-muted-foreground font-sans tabular-nums">
              {t('catalog.affected', { count })}
            </span>
          </li>
        ))}
      </ul>
    </StepRow>
  );
}

/** Automatic sync (D-185): what the store's own changes did on the network since the daily fetch. */
function PushFacts({ push }: { push: SocialCatalogPushStatus }) {
  const { t } = useTranslation('social');
  const format = useFormat();
  return (
    <section aria-label={t('catalog.push.title')} className="flex flex-col gap-2">
      <h4 className="text-sm font-medium">{t('catalog.push.title')}</h4>
      <p className="text-muted-foreground text-sm">{t('catalog.push.hint')}</p>
      <dl className="grid gap-3 sm:grid-cols-2">
        <StepRow label={t('catalog.push.lastSent')}>
          {push.lastPushedAt ? (
            <span className="flex flex-wrap items-center gap-2">
              <span>{format.dateTime(push.lastPushedAt)}</span>
              <span className="text-muted-foreground tabular-nums">
                {[
                  push.updated > 0 ? t('catalog.push.updated', { count: push.updated }) : null,
                  push.removed > 0 ? t('catalog.push.removed', { count: push.removed }) : null,
                  push.added > 0 ? t('catalog.push.added', { count: push.added }) : null,
                  push.imagesChanged > 0
                    ? t('catalog.push.imagesChanged', { count: push.imagesChanged })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">{t('catalog.push.nothingYet')}</span>
          )}
        </StepRow>
        {push.refetchPending ? (
          <StepRow label={t('catalog.push.waiting')}>
            <span className="flex flex-wrap items-center gap-2">
              <StepBadge tone="todo">{t('catalog.push.waitingBadge')}</StepBadge>
              <span className="text-muted-foreground">{t('catalog.push.waitingHint')}</span>
            </span>
          </StepRow>
        ) : null}
      </dl>
      {push.error ? (
        <p role="alert" className="text-destructive text-sm">
          {t('catalog.push.error', { message: push.error })}
        </p>
      ) : null}
      {push.failedItems.length > 0 ? (
        <div className="flex flex-col gap-1">
          <p className="text-sm">{t('catalog.push.refused', { count: push.failedItems.length })}</p>
          <ul className="flex flex-col gap-0.5">
            {push.failedItems.map((item) => (
              <li key={item.itemId} className="text-xs">
                <span className="font-mono">{item.itemId}</span>{' '}
                <span className="text-muted-foreground">
                  {item.errors
                    .map((error) =>
                      error.attribute ? `${error.attribute}: ${error.message}` : error.message,
                    )
                    .join('; ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/** Step 3: one switch registers the feed; the rest is what the network reports about it. */
export function CatalogStep({
  active,
  status,
  loading,
  error,
  onRetry,
  editable,
  toggling,
  onToggle,
  syncing,
  onSync,
  timeZone,
  websiteClaimed,
}: CatalogStepProps) {
  const { t } = useTranslation('social');
  const format = useFormat();
  const switchId = useId();
  const enabled = status?.enabled ?? false;

  const badge = !active ? (
    <StepBadge tone="todo">{t('steps.waiting')}</StepBadge>
  ) : enabled ? (
    <StepBadge tone="done">{t('steps.on')}</StepBadge>
  ) : (
    <StepBadge tone="todo">{t('steps.off')}</StepBadge>
  );

  return (
    <ChannelStep index={3} title={t('catalog.title')} badge={badge} disabled={!active}>
      <p className="text-muted-foreground text-sm">{t('catalog.hint')}</p>
      {!active ? null : loading ? (
        <Skeleton className="h-10" />
      ) : error ? (
        <ErrorState error={error} onRetry={onRetry} compact />
      ) : status ? (
        <>
          {!enabled && !websiteClaimed ? (
            <p className="text-muted-foreground text-xs">{t('catalog.websiteFirst')}</p>
          ) : null}
          <div className="flex items-center gap-3">
            <Switch
              id={switchId}
              checked={enabled}
              disabled={!editable || toggling}
              onCheckedChange={onToggle}
              aria-label={t('catalog.switch')}
            />
            <Label htmlFor={switchId} className="text-sm">
              {toggling
                ? t(enabled ? 'catalog.disabling' : 'catalog.enabling')
                : t('catalog.switch')}
            </Label>
          </div>
          {enabled ? (
            <>
              <dl className="grid gap-3 sm:grid-cols-2">
                <StepRow label={t('catalog.feedUrl')}>
                  {status.feedUrl ? (
                    <code className="bg-muted block rounded-md px-2 py-1.5 font-mono text-xs break-all select-all">
                      {status.feedUrl}
                    </code>
                  ) : (
                    '—'
                  )}
                </StepRow>
                <StepRow label={t('catalog.registeredAt')}>
                  {status.registeredAt ? format.dateTime(status.registeredAt) : '—'}
                </StepRow>
                <StepRow label={t('catalog.processingTime')}>
                  {t('catalog.processingTimeValue', {
                    time: status.processingTime,
                    zone: timeZone,
                  })}
                </StepRow>
                <StepRow label={t('catalog.lastProcessing')}>
                  {status.lastProcessing ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <StepBadge tone={RUN_TONE[status.lastProcessing.status]}>
                        {t(`catalog.processingStatus.${status.lastProcessing.status}`)}
                      </StepBadge>
                      <span>{format.dateTime(status.lastProcessing.startedAt)}</span>
                      {status.lastProcessing.counts ? (
                        <span className="text-muted-foreground tabular-nums">
                          {t('catalog.counts', status.lastProcessing.counts)}
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{t('catalog.noProcessing')}</span>
                  )}
                </StepRow>
                {status.lastProcessing ? (
                  <>
                    <Counts label={t('catalog.feedErrors')} counts={status.lastProcessing.errors} />
                    <Counts
                      label={t('catalog.feedWarnings')}
                      counts={status.lastProcessing.warnings}
                    />
                  </>
                ) : null}
              </dl>
              {status.utm ? (
                <p className="text-muted-foreground text-xs">{t('catalog.utmNote')}</p>
              ) : null}
              {status.push ? <PushFacts push={status.push} /> : null}
              <div className="flex flex-col gap-2">
                <h4 className="text-sm font-medium">{t('catalog.issues')}</h4>
                {status.issues.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('catalog.noIssues')}</p>
                ) : (
                  <CatalogIssuesTable issues={status.issues} />
                )}
              </div>
              {editable ? (
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={syncing}
                    onClick={onSync}
                  >
                    <RefreshCw aria-hidden className={syncing ? 'animate-spin' : undefined} />
                    {syncing ? t('catalog.syncing') : t('catalog.syncNow')}
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-muted-foreground text-xs">{t('catalog.off')}</p>
          )}
        </>
      ) : null}
    </ChannelStep>
  );
}
